// src/public/PublicDiscTestQuestions.js
import { useState, useEffect, useRef } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    IconButton,
    Alert,
    Chip,
    Dialog,
    DialogContent,
    CircularProgress,
} from '@mui/material';
import {
    NavigateNext as NextIcon,
    NavigateBefore as BackIcon,
    CheckCircle as CompleteIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { discPages, discPagesEn } from './utils/disc_test_bundle';

// El test publico corre en el navegador del candidato, muchas veces con
// conexion inestable. La instancia de axios no define `timeout`, y el default
// es esperar para siempre: sin esto, un backend colgado deja el modal de
// "enviando" girando sin fin. Se pasan por llamada y no en la instancia para no
// afectar las rutas lentas de la app (exportes, generacion de documentos).
const SAVE_TIMEOUT_MS = 15000;
const COMPLETE_TIMEOUT_MS = 45000;
const COMPLETE_ATTEMPTS = 2;

const STORAGE_PREFIX = 'amanda_disc_test_';

/**
 * Respaldo local de las respuestas.
 *
 * Es una red de seguridad para el caso en que el candidato recargue o cierre la
 * pestana con paginas que no alcanzaron a guardarse en el backend: sin esto se
 * pierden, porque solo vivian en memoria. Si localStorage no esta disponible el
 * test sigue funcionando igual.
 */
const readLocalBackup = (testId) => {
    try {
        const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${testId}`);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed?.responses) ? parsed.responses : [];
    } catch (err) {
        return [];
    }
};

const writeLocalBackup = (testId, responses) => {
    try {
        window.localStorage.setItem(
            `${STORAGE_PREFIX}${testId}`,
            JSON.stringify({ responses, updatedAt: Date.now() })
        );
    } catch (err) {
        // Sin respaldo local se sigue igual: es una red de seguridad, no un requisito.
    }
};

const clearLocalBackup = (testId) => {
    try {
        window.localStorage.removeItem(`${STORAGE_PREFIX}${testId}`);
    } catch (err) {
        // idem
    }
};

/**
 * El backend manda: lo local SOLO rellena las paginas que el servidor no tiene.
 * Asi una respuesta vieja guardada en el navegador nunca pisa una confirmada.
 */
const mergeWithBackup = (fromBackend, fromLocal) => {
    const byPage = new Map(fromBackend.map((r) => [r.page, r]));
    fromLocal.forEach((r) => {
        if (r && typeof r.page === 'number' && !byPage.has(r.page)) {
            byPage.set(r.page, r);
        }
    });
    return Array.from(byPage.values());
};

const PublicDiscTestQuestions = ({ testId, language = "es", initialTime = 0, onComplete, initialResponses = [], initialPage = 0 }) => {
    const { t } = useTranslation();

    const totalPages = discPages.length;

    const [responses, setResponses] = useState(() => mergeWithBackup(initialResponses, readLocalBackup(testId)));
    const [currentPage, setCurrentPage] = useState(Math.min(initialPage, totalPages - 1));
    const [mostSelected, setMostSelected] = useState(null);
    const [leastSelected, setLeastSelected] = useState(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [showCompletingModal, setShowCompletingModal] = useState(false);

    // Paginas que el backend ya confirmo. Todo lo que llego del servidor entra
    // como confirmado; lo que aporto el respaldo local, no, para que se reenvie.
    const confirmedPagesRef = useRef(new Set(initialResponses.map((r) => r.page)));

    // El cronometro vive aca y no en PublicDiscTest.js porque este es el unico
    // componente que tiene las respuestas en memoria: al agotarse el tiempo hay
    // que enviarlas. Antes el timer estaba en el padre y solo cambiaba de
    // pantalla, asi que el test quedaba para siempre en estado "started".
    const [timeRemaining, setTimeRemaining] = useState(initialTime);

    // Momento exacto en que vence el test, anclado al reloj. El conteo NO
    // resta 1 por tick: los navegadores estrangulan los timers de pestanas
    // en segundo plano (hasta un tick por minuto), y restando por tick el
    // cronometro se pausaba al cambiar de pestana, estirando el tiempo real
    // del candidato. Calculando contra el reloj, al volver (o en el propio
    // tick estrangulado) el conteo se corrige solo y dispara el cierre si
    // ya vencio.
    const deadlineRef = useRef(Date.now() + (initialTime || 0) * 1000);
    const timerRef = useRef(null);

    // Evita el doble envio cuando el cronometro llega a 0 justo mientras el
    // candidato apreto "finalizar" (o al reves).
    const completingRef = useRef(false);

    // Ref que siempre apunta a la version mas reciente de handleAutoComplete.
    // El setInterval se crea una sola vez al montar y su closure queda congelada
    // en el primer render: llamar handleAutoComplete directo usaria `responses`
    // y `currentPage` iniciales y enviaria el test vacio. Se reasigna en cada
    // render (ver la linea despues de la definicion del handler).
    const handleAutoCompleteRef = useRef(null);

    const currentPageData = language === "es" ? discPages[currentPage] : discPagesEn[currentPage];

    useEffect(() => {
        const savedResponse = responses.find(r => r.page === currentPage + 1);
        if (savedResponse) {
            setMostSelected(savedResponse.mostIndex);
            setLeastSelected(savedResponse.leastIndex);
        } else {
            setMostSelected(null);
            setLeastSelected(null);
        }
        setError('');
    }, [currentPage, responses]);

    // Timer countdown (anclado al reloj, no al numero de ticks)
    useEffect(() => {
        timerRef.current = setInterval(() => {
            const remaining = Math.max(0, Math.round((deadlineRef.current - Date.now()) / 1000));
            setTimeRemaining(remaining);

            if (remaining <= 0) {
                clearInterval(timerRef.current);
                // Usar el ref para invocar la version vigente del handler
                handleAutoCompleteRef.current?.();
            }
        }, 1000);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    const formatTime = (seconds) => {
        const safeSeconds = Math.max(0, seconds);
        const mins = Math.floor(safeSeconds / 60);
        const secs = safeSeconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleMostClick = (index) => {
        if (index === leastSelected) {
            setLeastSelected(null);
        }
        setMostSelected(index);
        setError('');
    };

    const handleLeastClick = (index) => {
        if (index === mostSelected) {
            setMostSelected(null);
        }
        setLeastSelected(index);
        setError('');
    };

    /**
     * Deja la respuesta de la pagina actual en el estado y en el respaldo local.
     * Devuelve el arreglo actualizado (o null si falta marcar algo), porque los
     * llamadores necesitan usarlo de inmediato y `setResponses` no es sincrono.
     */
    const saveCurrentResponse = () => {
        if (mostSelected === null || leastSelected === null) {
            setError(t('disc.mustSelectBothOptions'));
            return null;
        }

        const pageNumber = currentPage + 1;
        const previous = responses.find(r => r.page === pageNumber);
        const unchanged = previous
            && previous.mostIndex === mostSelected
            && previous.leastIndex === leastSelected;

        // Si cambio la respuesta hay que reenviarla aunque ya estuviera guardada
        if (previous && !unchanged) {
            confirmedPagesRef.current.delete(pageNumber);
        }

        const newResponse = {
            page: pageNumber,
            mostIndex: mostSelected,
            leastIndex: leastSelected,
            // Momento real en que se respondio. Viaja al backend porque una
            // pagina rescatada tarde quedaria con un timestamp posterior al de
            // la siguiente, y el indice de validez mide el tiempo por pregunta
            // restando timestamps consecutivos.
            answeredAt: unchanged ? previous.answeredAt : Date.now(),
        };

        const updated = [...responses.filter(r => r.page !== pageNumber), newResponse];

        setResponses(updated);
        writeLocalBackup(testId, updated);

        return updated;
    };

    /**
     * Guarda en el backend TODAS las paginas que aun no estan confirmadas, no
     * solo la actual.
     *
     * Antes cada guardado mandaba unicamente su pagina: si esa request se caia,
     * esa respuesta no se reintentaba nunca mas y se perdia en silencio. Con el
     * arrastre, el siguiente avance es el reintento y la pagina huerfana se
     * recupera sola, sin que el candidato se entere.
     */
    const persistResponses = async (allResponses) => {
        const pending = allResponses.filter(r => !confirmedPagesRef.current.has(r.page));
        if (pending.length === 0) return true;

        try {
            await api.put(
                `/disc-tests-public/${testId}/page-results`,
                {
                    pages: pending.map(r => ({
                        pageNumber: r.page,
                        answers: { mostIndex: r.mostIndex, leastIndex: r.leastIndex },
                        answeredAt: r.answeredAt,
                    })),
                },
                { timeout: SAVE_TIMEOUT_MS }
            );

            pending.forEach(r => confirmedPagesRef.current.add(r.page));
            return true;
        } catch (err) {
            console.error('Error saving page results:', err);
            return false;
        }
    };

    /**
     * Cierra el test en el backend. Reintenta, porque este es el ultimo punto
     * donde se puede salvar todo lo respondido.
     *
     * Si el primer intento llego al servidor pero se perdio la respuesta, el
     * reintento devuelve 400 diciendo que el test ya no esta en "started": eso
     * es exito, no error.
     */
    const submitTest = async (allResponses) => {
        for (let attempt = 0; attempt < COMPLETE_ATTEMPTS; attempt += 1) {
            try {
                const response = await api.put(
                    `/disc-tests-public/${testId}/complete`,
                    { responses: allResponses },
                    { timeout: COMPLETE_TIMEOUT_MS }
                );

                if (response.data.errorCode === 0) return true;
            } catch (err) {
                const currentStatus = err.response?.data?.response?.currentStatus;
                if (err.response?.status === 400 && currentStatus && currentStatus !== 'started') {
                    // Ya se habia cerrado en un intento anterior
                    return true;
                }
                console.error('Error completing test:', err);
            }
        }

        return false;
    };

    const handleNext = async () => {
        const updated = saveCurrentResponse();
        if (!updated) return;

        await persistResponses(updated);

        if (currentPage < totalPages - 1) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handleBack = () => {
        saveCurrentResponse();
        if (currentPage > 0) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const updateCurrentPageInBackend = async (pageNumber) => {
        try {
            await api.put(
                `/disc-tests-public/${testId}/current-page`,
                { pageNumber },
                { timeout: SAVE_TIMEOUT_MS }
            );
        } catch (err) {
            console.error('Error updating current page:', err);
        }
    };

    const handleComplete = async () => {
        if (completingRef.current) return;

        const updated = saveCurrentResponse();
        if (!updated) return;

        completingRef.current = true;
        if (timerRef.current) clearInterval(timerRef.current);

        setSaving(true);
        setShowCompletingModal(true);

        try {
            await persistResponses(updated);

            if (await submitTest(updated)) {
                clearLocalBackup(testId);
                onComplete({ completed: true });
            } else {
                completingRef.current = false;
                setError(t('disc.errorCompletingTest'));
                setShowCompletingModal(false);
            }
        } catch (err) {
            console.error('Error completing test:', err);
            completingRef.current = false;
            setError(t('disc.errorCompletingTest'));
            setShowCompletingModal(false);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Cierre automatico al agotarse el cronometro.
     *
     * Envia lo que el candidato alcanzo a responder (puede ser menos de las 24
     * paginas) para que el test no quede colgado en estado "started". Salvo que
     * no haya respondido ninguna: en ese caso el backend no puede calcular
     * scores, asi que solo se le muestra el mensaje de tiempo agotado.
     *
     * Si el envio falla se avisa igual, pero con otro mensaje: la pantalla no
     * puede asegurarle que se guardaron sus respuestas si no fue asi.
     */
    const handleAutoComplete = async () => {
        if (completingRef.current) return;
        completingRef.current = true;

        if (timerRef.current) clearInterval(timerRef.current);

        setSaving(true);
        setShowCompletingModal(true);

        let submitted = false;
        // Con 0 respuestas no se llama a /complete (no hay nada que calcular),
        // y eso NO es un envio fallido: el mensaje final debe ser el de tiempo
        // agotado a secas, no el de "no pudimos registrar tus respuestas".
        let hadAnswers = false;

        try {
            const pageNumber = currentPage + 1;
            const hasCurrentAnswer = mostSelected !== null && leastSelected !== null;

            // La pagina en curso solo cuenta si alcanzo a marcar ambas opciones
            const allResponses = hasCurrentAnswer
                ? [
                    ...responses.filter(r => r.page !== pageNumber),
                    { page: pageNumber, mostIndex: mostSelected, leastIndex: leastSelected, answeredAt: Date.now() },
                ]
                : [...responses];

            hadAnswers = allResponses.length > 0;

            if (hadAnswers) {
                await persistResponses(allResponses);
                submitted = await submitTest(allResponses);

                if (submitted) clearLocalBackup(testId);
            }
        } catch (err) {
            // Aunque falle el envio hay que sacar al candidato de la pantalla de
            // preguntas: su tiempo ya termino y no puede seguir respondiendo.
            console.error('Error completing test by timeout:', err);
        } finally {
            setSaving(false);
            onComplete({ completed: true, expiredByTime: true, submitFailed: hadAnswers && !submitted });
        }
    };

    // Reasignar en cada render para que el interval siempre ejecute la version
    // vigente del handler (con responses/currentPage/selecciones actuales)
    handleAutoCompleteRef.current = handleAutoComplete;

    useEffect(() => {
        updateCurrentPageInBackend(currentPage + 1);
    }, [currentPage]);

    const isLastPage = currentPage === totalPages - 1;

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h5" align="center" color="primary" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                    {t('disc.page')} {currentPage + 1} / {totalPages}
                </Typography>

                <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mb: 3, textAlign: 'center' }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                        <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            <AddIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                            {t('disc.mostDescribesMe')}
                        </Box>
                        {' | '}
                        <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
                            <RemoveIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                            {t('disc.leastDescribesMe')}
                        </Box>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('disc.instructionText')}
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                    {currentPageData.options.map((option, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <IconButton
                                onClick={() => handleMostClick(index)}
                                color="primary"
                                sx={{
                                    border: '2px solid',
                                    borderColor: mostSelected === index ? 'primary.main' : 'divider',
                                    bgcolor: mostSelected === index ? 'primary.main' : 'transparent',
                                    color: mostSelected === index ? 'white' : 'primary.main',
                                    '&:hover': {
                                        bgcolor: mostSelected === index ? 'primary.dark' : 'action.hover',
                                    },
                                }}
                            >
                                <AddIcon />
                            </IconButton>

                            <IconButton
                                onClick={() => handleLeastClick(index)}
                                color="error"
                                sx={{
                                    border: '2px solid',
                                    borderColor: leastSelected === index ? 'error.main' : 'divider',
                                    bgcolor: leastSelected === index ? 'error.main' : 'transparent',
                                    color: leastSelected === index ? 'white' : 'error.main',
                                    '&:hover': {
                                        bgcolor: leastSelected === index ? 'error.dark' : 'action.hover',
                                    },
                                }}
                            >
                                <RemoveIcon />
                            </IconButton>

                            <Typography variant="body1" sx={{ flex: 1 }}>
                                {option.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                        variant="outlined"
                        startIcon={<BackIcon />}
                        onClick={handleBack}
                        disabled={currentPage === 0}
                        sx={{ textTransform: 'none' }}
                    >
                        {t('disc.previous')}
                    </Button>

                    <Chip
                        label={formatTime(timeRemaining)}
                        color={timeRemaining < 300 ? 'error' : 'primary'}
                        sx={{ fontSize: '1rem', py: 2 }}
                    />

                    {isLastPage ? (
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<CompleteIcon />}
                            onClick={handleComplete}
                            disabled={saving || mostSelected === null || leastSelected === null}
                            sx={{ textTransform: 'none' }}
                        >
                            {saving ? t('disc.completing') : t('disc.completeTest')}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            endIcon={<NextIcon />}
                            onClick={handleNext}
                            disabled={mostSelected === null || leastSelected === null}
                            sx={{ textTransform: 'none' }}
                        >
                            {t('disc.next')}
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* Modal de completando test */}
            <Dialog
                open={showCompletingModal}
                disableEscapeKeyDown
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    style: {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(5px)',
                    }
                }}
                slotProps={{
                    backdrop: {
                        style: {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        }
                    }
                }}
            >
                <DialogContent sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress size={60} sx={{ mb: 3 }} />
                    <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                        {t('publicEvaluations.completingEvaluation')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('publicEvaluations.processingResponses')}
                        <br />
                        {t('publicEvaluations.operationTime')}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
                        {t('publicEvaluations.doNotClose')}
                    </Typography>
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default PublicDiscTestQuestions;
